import React, { useState, useEffect, useRef, useContext } from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Paper, 
  Avatar, 
  CssBaseline, 
  CircularProgress,
  Alert,
  AlertTitle,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Collapse,
  Card,
  CardContent,
  Divider,
  Button,
  Slide,
  IconButton,
  useMediaQuery
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import ScheduleIcon from '@mui/icons-material/Schedule';
import DescriptionIcon from '@mui/icons-material/Description';
import InventoryIcon from '@mui/icons-material/Inventory';
import WarningIcon from '@mui/icons-material/Warning';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import SyncIcon from '@mui/icons-material/Sync';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CelebrationIcon from '@mui/icons-material/Celebration';
import CloseIcon from '@mui/icons-material/Close';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import FiberNewIcon from '@mui/icons-material/FiberNew';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WavingHandIcon from '@mui/icons-material/WavingHand';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import LinkIcon from '@mui/icons-material/Link';
import { 
  CreditCard, 
  Payment as PaymentIcon,
  Launch as LaunchIcon 
} from '@mui/icons-material';
import { ThemeProvider, createTheme, useTheme } from '@mui/material/styles';
import { styled, alpha } from '@mui/system';
import { Link } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { ThemeContext } from '../ThemeContext';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://inkedupmanager.com/api';

// ─── Helper: time-of-day greeting ────────────────────────────────────────────
const getGreeting = () => {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
};

// ─── Styled Components ───────────────────────────────────────────────────────

// Modern stat card — clean, elevated, accent color on icon only
const StatCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2.5),
  borderRadius: 16,
  background: theme.palette.mode === 'dark' 
    ? 'linear-gradient(145deg, #1e1e1e 0%, #262626 100%)'
    : '#ffffff',
  border: theme.palette.mode === 'dark'
    ? '1px solid rgba(255,255,255,0.06)'
    : '1px solid rgba(0,0,0,0.06)',
  boxShadow: theme.palette.mode === 'dark'
    ? '0 1px 3px rgba(0,0,0,0.4)'
    : '0 1px 3px rgba(0,0,0,0.06), 0 4px 12px rgba(0,0,0,0.04)',
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  cursor: 'pointer',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.5),
  position: 'relative',
  overflow: 'hidden',
  '&:hover': {
    transform: 'translateY(-3px)',
    boxShadow: theme.palette.mode === 'dark'
      ? '0 8px 24px rgba(0,0,0,0.5)'
      : '0 8px 24px rgba(0,0,0,0.08)',
  },
}));

const StatIconWrap = styled(Box)(({ bgcolor }) => ({
  width: 44,
  height: 44,
  borderRadius: 12,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: bgcolor || 'rgba(255,23,68,0.1)',
  flexShrink: 0,
}));

// Section card (appointments, low-stock, stripe)
const SectionCard = styled(Paper)(({ theme }) => ({
  borderRadius: 16,
  background: theme.palette.mode === 'dark'
    ? 'linear-gradient(145deg, #1e1e1e 0%, #262626 100%)'
    : '#ffffff',
  border: theme.palette.mode === 'dark'
    ? '1px solid rgba(255,255,255,0.06)'
    : '1px solid rgba(0,0,0,0.06)',
  boxShadow: theme.palette.mode === 'dark'
    ? '0 1px 3px rgba(0,0,0,0.4)'
    : '0 1px 3px rgba(0,0,0,0.06), 0 4px 12px rgba(0,0,0,0.04)',
  overflow: 'hidden',
  transition: 'box-shadow 0.2s ease',
  '&:hover': {
    boxShadow: theme.palette.mode === 'dark'
      ? '0 6px 20px rgba(0,0,0,0.5)'
      : '0 6px 20px rgba(0,0,0,0.07)',
  },
}));

// Welcome banner
const WelcomeBanner = styled(Alert)(({ theme }) => ({
  borderRadius: 16,
  background: theme.palette.mode === 'dark'
    ? 'linear-gradient(135deg, #1b5e20 0%, #2e7d32 100%)'
    : 'linear-gradient(135deg, #43a047 0%, #66bb6a 100%)',
  color: '#fff',
  border: 'none',
  '& .MuiAlert-icon': { color: '#fff', fontSize: '1.6rem' },
  '& .MuiAlert-action': { color: '#fff' },
  boxShadow: theme.palette.mode === 'dark'
    ? '0 4px 16px rgba(27,94,32,0.35)'
    : '0 4px 16px rgba(67,160,71,0.25)',
}));

// Appointment list item
const AppointmentListItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(1.5),
  padding: theme.spacing(1.5),
  borderRadius: 12,
  background: theme.palette.mode === 'dark'
    ? 'rgba(255,255,255,0.03)'
    : 'rgba(0,0,0,0.02)',
  border: theme.palette.mode === 'dark'
    ? '1px solid rgba(255,255,255,0.05)'
    : '1px solid rgba(0,0,0,0.04)',
  transition: 'background 0.15s ease',
  '&:hover': {
    background: theme.palette.mode === 'dark'
      ? 'rgba(255,255,255,0.06)'
      : 'rgba(0,0,0,0.035)',
  },
}));

// Stock item row
const StockRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  padding: theme.spacing(1.5),
  borderRadius: 12,
  background: theme.palette.mode === 'dark'
    ? 'rgba(255,152,0,0.08)'
    : 'rgba(255,152,0,0.06)',
  border: theme.palette.mode === 'dark'
    ? '1px solid rgba(255,152,0,0.2)'
    : '1px solid rgba(255,152,0,0.15)',
}));

// New-count badge (pulsing)
const PulseBadge = styled(Box)(() => ({
  position: 'absolute',
  top: 12,
  right: 12,
  backgroundColor: '#ff1744',
  color: '#fff',
  borderRadius: '50%',
  width: 24,
  height: 24,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '0.7rem',
  fontWeight: 700,
  boxShadow: '0 2px 8px rgba(255,23,68,0.4)',
  animation: 'pulseBadge 2s infinite',
  '@keyframes pulseBadge': {
    '0%':  { boxShadow: '0 0 0 0 rgba(255,23,68,0.45)' },
    '70%': { boxShadow: '0 0 0 8px rgba(255,23,68,0)' },
    '100%':{ boxShadow: '0 0 0 0 rgba(255,23,68,0)' },
  },
}));

// Quick-action card for Stripe etc
const ActionCard = styled(Paper)(({ theme, accentcolor }) => ({
  borderRadius: 16,
  padding: theme.spacing(2.5),
  background: theme.palette.mode === 'dark'
    ? 'linear-gradient(145deg, #1e1e1e 0%, #262626 100%)'
    : '#ffffff',
  border: theme.palette.mode === 'dark'
    ? '1px solid rgba(255,255,255,0.06)'
    : '1px solid rgba(0,0,0,0.06)',
  boxShadow: theme.palette.mode === 'dark'
    ? '0 1px 3px rgba(0,0,0,0.4)'
    : '0 1px 3px rgba(0,0,0,0.06), 0 4px 12px rgba(0,0,0,0.04)',
  cursor: 'pointer',
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.5),
  position: 'relative',
  overflow: 'hidden',
  '&:hover': {
    transform: 'translateY(-3px)',
    boxShadow: theme.palette.mode === 'dark'
      ? '0 8px 24px rgba(0,0,0,0.5)'
      : '0 8px 24px rgba(0,0,0,0.08)',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: 3,
    background: accentcolor || '#ff1744',
  },
}));

// ─── Dashboard Component ─────────────────────────────────────────────────────

const Dashboard = () => {
  const { getAccessTokenSilently, isAuthenticated, isLoading: auth0Loading, user } = useAuth0();
  const themeCtx = useContext(ThemeContext);
  const muiTheme = useTheme();
  const isMobile = useMediaQuery('(max-width:600px)');
  const isTablet = useMediaQuery('(max-width:960px)');

  // ── State (unchanged) ────────────────────────────────────────────────────
  const [totalClients, setTotalClients] = useState(0);
  const [totalAppointments, setTotalAppointments] = useState(0);
  const [inventoryCount, setInventoryCount] = useState(0);
  const [inventoryData, setInventoryData] = useState([]);
  const [appointmentsData, setAppointmentsData] = useState([]);
  const [upcomingAppointments, setUpcomingAppointments] = useState([]);
  const [lowStockItems, setLowStockItems] = useState([]);
  const [userName, setUserName] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [showLowStockDetails, setShowLowStockDetails] = useState(false);
  const [showAppointmentDetails, setShowAppointmentDetails] = useState(false);
  const [error, setError] = useState(null);

  // Tattoo Inquiries state
  const [totalInquiries, setTotalInquiries] = useState(0);
  const [newInquiriesCount, setNewInquiriesCount] = useState(0);

  // Stripe Connect state
  const [stripeConnected, setStripeConnected] = useState(false);
  const [stripeAccountId, setStripeAccountId] = useState(null);
  const [checkingStripeStatus, setCheckingStripeStatus] = useState(true);

  // Subscription sync states (hidden from UI but still functional)
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);

  // First-time subscriber welcome states
  const [showWelcomeMessage, setShowWelcomeMessage] = useState(false);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [isFirstTimeUser, setIsFirstTimeUser] = useState(false);

  // Refs
  const isMounted = useRef(true);
  const dataLoaded = useRef(false);

  const namespace = 'https://inkedupmanager.com';

  // ── Effects & Logic (ALL unchanged) ──────────────────────────────────────

  useEffect(() => {
    return () => { isMounted.current = false; };
  }, []);

  // Stripe Connect functions
  const checkStripeConnection = async () => {
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE_URL}/stripe/connect/status/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });
      if (response.ok) {
        const data = await response.json();
        setStripeConnected(data.connected);
        setStripeAccountId(data.account_id);
      }
    } catch (error) {
      console.error('Error checking Stripe connection:', error);
    } finally {
      setCheckingStripeStatus(false);
    }
  };

  const handleStripeConnect = () => {
    if (stripeConnected) {
      window.open('https://dashboard.stripe.com/', '_blank');
    } else {
      const clientId = process.env.REACT_APP_STRIPE_CONNECT_CLIENT_ID;
      const redirectUri = encodeURIComponent(`${process.env.REACT_APP_API_BASE_URL}/stripe/connect/callback/`);
      const connectUrl = `https://connect.stripe.com/oauth/authorize?response_type=code&client_id=${clientId}&scope=read_write&redirect_uri=${redirectUri}`;
      window.location.href = connectUrl;
    }
  };

  const handleCreatePayment = () => {
    if (stripeConnected) {
      window.open('https://dashboard.stripe.com/payment-links', '_blank');
    } else {
      alert('Please connect your Stripe account first to create payment links.');
    }
  };

  // Check if user is first-time subscriber
  const checkFirstTimeUser = async () => {
    try {
      const token = await getAccessTokenSilently();
      const tokenPayload = JSON.parse(atob(token.split('.')[1]));
      const jwtRoles = tokenPayload[`${namespace}/roles`] || [];

      const response = await fetch(`${API_BASE_URL}/subscription/details/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSubscriptionData(data);

        const hasSubscription = data.has_subscription && data.tier && data.tier !== 'unpaid';
        const hasJWTRoles = jwtRoles.length > 0;

        if (hasSubscription && !hasJWTRoles) {
          setIsFirstTimeUser(true);
          setShowWelcomeMessage(true);
          setTimeout(() => { setShowWelcomeMessage(false); }, 30000);
        }
      }
    } catch (error) {
      console.error('Error checking first-time user status:', error);
    }
  };

  useEffect(() => {
    if (isAuthenticated && !auth0Loading && !dataLoaded.current) {
      checkFirstTimeUser();
      syncSubscriptionOnLoad();
    }
  }, [isAuthenticated, auth0Loading]);

  const syncSubscriptionOnLoad = async () => {
    try {
      setSyncing(true);
      const token = await getAccessTokenSilently({
        authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE },
      });

      const response = await fetch(`${API_BASE_URL}/verify-sync-subscription/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) throw new Error(`Sync API error: ${response.status}`);

      const syncResult = await response.json();
      setSyncStatus(syncResult);

      if (syncResult.synced && syncResult.action === 'updated_auth0_to_match_stripe') {
        setTimeout(() => { window.location.reload(); }, 1000);
      }
    } catch (error) {
      console.error('Subscription sync failed:', error);
      setSyncStatus({ error: error.message });
    } finally {
      setSyncing(false);
      fetchDashboardData();
    }
  };

  const getPlanDisplayName = (tier) => {
    const planNames = {
      'basic': 'Basic',
      'standard': 'Standard',
      'starter_studio': 'Starter Studio',
      'growth_studio': 'Growth Studio'
    };
    return planNames[tier] || tier;
  };

  const checkLowStockTattoo = (inventory) => {
    if (!Array.isArray(inventory)) return [];
    return inventory.filter(item => {
      const currentStock = parseInt(item.current_stock) || 0;
      const threshold = parseInt(item.low_stock_threshold) || 5;
      return currentStock <= threshold && currentStock >= 0;
    }).sort((a, b) => (parseInt(a.current_stock) || 0) - (parseInt(b.current_stock) || 0));
  };

  const getUpcomingAppointments = (appointments) => {
    if (!Array.isArray(appointments)) return [];
    const now = new Date();
    const nextWeek = new Date();
    nextWeek.setDate(now.getDate() + 7);
    return appointments
      .filter(appointment => {
        const appointmentDate = new Date(appointment.date);
        return appointmentDate >= now && appointmentDate <= nextWeek;
      })
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .slice(0, 5);
  };

  const getStockLevel = (item) => {
    const qty = parseInt(item.current_stock) || 0;
    const threshold = parseInt(item.low_stock_threshold) || 5;
    if (qty === 0) return { level: 'Out of Stock', color: 'error' };
    if (qty <= Math.min(2, threshold / 2)) return { level: 'Critical', color: 'error' };
    if (qty <= threshold) return { level: 'Low', color: 'warning' };
    return { level: 'Normal', color: 'success' };
  };

  const formatAppointmentDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = date - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    if (diffDays <= 7) return `In ${diffDays} days`;
    return date.toLocaleDateString();
  };

  const formatAppointmentTime = (dateString, timeString) => {
    if (timeString) return timeString;
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const fetchDashboardData = async () => {
    if (dataLoaded.current) return;
    try {
      setIsLoading(true);
      setError(null);

      const token = await getAccessTokenSilently({
        authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE },
      });

      const headers = {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      };

      const [clientsResponse, appointmentsResponse, inventoryResponse, userResponse, inquiriesResponse] = await Promise.all([
        fetch(`${API_BASE_URL}/clients/`, { headers }).then(res => {
          if (!res.ok) throw new Error(`Clients API error: ${res.status}`);
          return res.json();
        }).catch(error => {
          console.error("Dashboard: Clients Error:", error);
          return [];
        }),
        fetch(`${API_BASE_URL}/appointments/`, { headers }).then(res => {
          if (!res.ok) throw new Error(`Appointments API error: ${res.status}`);
          return res.json();
        }).catch(error => {
          console.error("Dashboard: Appointments Error:", error);
          return [];
        }),
        fetch(`${API_BASE_URL}/inventory/tattoo/`, { headers }).then(res => {
          if (!res.ok) throw new Error(`Tattoo Inventory API error: ${res.status}`);
          return res.json();
        }).then(data => {
          if (Array.isArray(data) && data.length > 0) { /* ok */ }
          return data;
        }).catch(error => {
          console.error("Dashboard: Tattoo Inventory Error:", error);
          return [];
        }),
        fetch(`${API_BASE_URL}/user/profile/`, { headers }).then(res => {
          if (!res.ok) throw new Error(`User Profile API error: ${res.status}`);
          return res.json();
        }).catch(error => {
          console.error("Dashboard: User Profile Error:", error);
          return {};
        }),
        fetch(`${API_BASE_URL}/contact/inquiries/`, { headers }).then(res => {
          if (!res.ok) return { inquiries: [], total_count: 0 };
          return res.json();
        }).catch(error => {
          console.error("Dashboard: Inquiries Error:", error);
          return { inquiries: [], total_count: 0 };
        })
      ]);

      dataLoaded.current = true;
      if (!isMounted.current) return;

      console.log("Dashboard: Dashboard data received:", {
        clients: Array.isArray(clientsResponse) ? clientsResponse.length : 'not an array',
        appointments: Array.isArray(appointmentsResponse) ? appointmentsResponse.length : 'not an array',
        inventory: Array.isArray(inventoryResponse) ? inventoryResponse.length : 'not an array',
        user: userResponse,
        inquiries: inquiriesResponse
      });

      if (userResponse && (userResponse.first_name || userResponse.last_name)) {
        setUserName(`${userResponse.first_name || ''} ${userResponse.last_name || ''}`);
      }

      let clientsArray = [];
      if (Array.isArray(clientsResponse)) {
        clientsArray = clientsResponse;
      } else if (clientsResponse && clientsResponse.clients && Array.isArray(clientsResponse.clients)) {
        clientsArray = clientsResponse.clients;
      } else {
        console.error("Dashboard: Unexpected clients response format:", clientsResponse);
        clientsArray = [];
      }
      setTotalClients(clientsArray.length);

      if (Array.isArray(appointmentsResponse)) {
        setTotalAppointments(appointmentsResponse.length);
        setAppointmentsData(appointmentsResponse);
        const upcoming = getUpcomingAppointments(appointmentsResponse);
        setUpcomingAppointments(upcoming);
      }

      if (Array.isArray(inventoryResponse)) {
        setInventoryCount(inventoryResponse.length);
        setInventoryData(inventoryResponse);
        const lowStock = checkLowStockTattoo(inventoryResponse);
        setLowStockItems(lowStock);
      }

      if (inquiriesResponse) {
        const inquiries = inquiriesResponse.inquiries || [];
        const total = inquiriesResponse.total_count || inquiries.length || 0;
        setTotalInquiries(total);
        const newCount = inquiries.filter(inq => inq.status === 'new' || !inq.is_read).length;
        setNewInquiriesCount(newCount);
      }

      checkStripeConnection();
    } catch (error) {
      console.error("Dashboard: Error fetching dashboard data:", error);
      setError(error.message);
    } finally {
      if (isMounted.current) {
        setIsLoading(false);
      }
    }
  };

  // ── Color helpers for stat cards ─────────────────────────────────────────
  const statCardConfig = [
    {
      label: 'Total Clients',
      value: totalClients,
      icon: <PeopleIcon sx={{ fontSize: 22, color: '#ff1744' }} />,
      iconBg: 'rgba(255,23,68,0.1)',
      link: '/clients',
      emptyText: 'Add your first client',
    },
    {
      label: 'Appointments',
      value: totalAppointments,
      icon: <ScheduleIcon sx={{ fontSize: 22, color: '#2979ff' }} />,
      iconBg: 'rgba(41,121,255,0.1)',
      link: '/appointments',
      emptyText: 'Schedule an appointment',
    },
    {
      label: 'Consent Forms',
      value: null, // no count for this one
      icon: <DescriptionIcon sx={{ fontSize: 22, color: '#00bfa5' }} />,
      iconBg: 'rgba(0,191,165,0.1)',
      link: '/consent-forms',
      emptyText: 'Manage forms',
    },
    {
      label: 'Inventory',
      value: inventoryCount,
      icon: <InventoryIcon sx={{ fontSize: 22, color: '#ff6d00' }} />,
      iconBg: 'rgba(255,109,0,0.1)',
      link: '/inventory',
      emptyText: 'Track your supplies',
    },
    {
      label: 'Inquiries',
      value: totalInquiries,
      icon: <QuestionAnswerIcon sx={{ fontSize: 22, color: '#7c4dff' }} />,
      iconBg: 'rgba(124,77,255,0.1)',
      link: '/tattoo-inquiries',
      emptyText: 'No inquiries yet',
      badge: newInquiriesCount,
    },
  ];

  // ── Render ───────────────────────────────────────────────────────────────
  const isDark = themeCtx?.darkMode;

  return (
    <Box
      sx={{
        px: { xs: 2, sm: 3, md: 4 },
        py: { xs: 2, sm: 3 },
        minHeight: '100vh',
        maxWidth: 1200,
        mx: 'auto',
      }}
    >
      {/* ─── Welcome Banner (first-time subscriber) ─────────────────── */}
      <Slide direction="down" in={showWelcomeMessage} mountOnEnter unmountOnExit>
        <WelcomeBanner
          icon={<CelebrationIcon />}
          action={
            <IconButton
              size="small"
              sx={{ color: '#fff' }}
              onClick={() => setShowWelcomeMessage(false)}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          }
          sx={{ mb: 3 }}
        >
          <AlertTitle sx={{ color: '#fff', fontWeight: 700, fontSize: '1rem' }}>
            Welcome to {subscriptionData ? getPlanDisplayName(subscriptionData.tier) : 'Your Subscription'}!
          </AlertTitle>
          <Typography sx={{ color: '#fff', fontSize: '0.875rem' }}>
            Your subscription is active — you have full access to all features.
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.8rem', mt: 0.5 }}>
            Permissions will fully synchronize on your next login.
          </Typography>
        </WelcomeBanner>
      </Slide>

      {/* ─── Loading ────────────────────────────────────────────────── */}
      {(auth0Loading || isLoading) && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress size={48} sx={{ color: '#ff1744' }} />
        </Box>
      )}

      {/* ─── Error ──────────────────────────────────────────────────── */}
      {error && !isLoading && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }}>
          <AlertTitle>Error Loading Dashboard</AlertTitle>
          {error}
        </Alert>
      )}

      {/* ─── Main Content ───────────────────────────────────────────── */}
      {isAuthenticated && !auth0Loading && !isLoading && (
        <>
          {/* ── Greeting Header ──────────────────────────────────────── */}
          <Box sx={{ mb: { xs: 3, md: 4 } }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                color: 'text.primary',
                lineHeight: 1.3,
              }}
            >
              {getGreeting()}{userName ? `, ${userName.trim().split(' ')[0]}` : ''}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: 'text.secondary',
                mt: 0.5,
                fontSize: { xs: '0.875rem', sm: '0.95rem' },
              }}
            >
              Here's what's happening with your studio today.
            </Typography>
          </Box>

          {/* ── Stat Cards ───────────────────────────────────────────── */}
          <Grid container spacing={2} sx={{ mb: { xs: 3, md: 4 } }}>
            {statCardConfig.map((stat) => (
              <Grid item xs={6} sm={4} md key={stat.label}>
                <Link to={stat.link} style={{ textDecoration: 'none' }}>
                  <StatCard>
                    {/* Badge */}
                    {stat.badge > 0 && <PulseBadge>{stat.badge}</PulseBadge>}

                    <StatIconWrap bgcolor={stat.iconBg}>{stat.icon}</StatIconWrap>

                    <Box>
                      <Typography
                        sx={{
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          color: 'text.secondary',
                          textTransform: 'uppercase',
                          letterSpacing: '0.04em',
                        }}
                      >
                        {stat.label}
                      </Typography>

                      {stat.value !== null ? (
                        <Typography
                          sx={{
                            fontSize: { xs: '1.5rem', sm: '1.75rem' },
                            fontWeight: 700,
                            color: 'text.primary',
                            lineHeight: 1.2,
                          }}
                        >
                          {stat.value}
                        </Typography>
                      ) : (
                        <Typography
                          sx={{
                            fontSize: '0.8rem',
                            color: 'text.secondary',
                            mt: 0.25,
                          }}
                        >
                          {stat.emptyText}
                        </Typography>
                      )}

                      {stat.badge > 0 && (
                        <Chip
                          label={`${stat.badge} new`}
                          size="small"
                          sx={{
                            mt: 0.75,
                            height: 20,
                            fontSize: '0.65rem',
                            fontWeight: 700,
                            backgroundColor: 'rgba(255,23,68,0.1)',
                            color: '#ff1744',
                          }}
                        />
                      )}
                    </Box>
                  </StatCard>
                </Link>
              </Grid>
            ))}
          </Grid>

          {/* ── Payments & Stripe Section ─────────────────────────────── */}
          <Grid container spacing={2} sx={{ mb: { xs: 3, md: 4 } }}>
            <Grid item xs={12} sm={stripeConnected ? 4 : 12}>
              <ActionCard
                accentcolor={stripeConnected ? '#43a047' : '#ff1744'}
                onClick={handleStripeConnect}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <StatIconWrap bgcolor={stripeConnected ? 'rgba(67,160,71,0.1)' : 'rgba(255,23,68,0.1)'}>
                    <CreditCard sx={{ fontSize: 22, color: stripeConnected ? '#43a047' : '#ff1744' }} />
                  </StatIconWrap>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: 'text.primary' }}>
                      {checkingStripeStatus ? 'Checking...' : stripeConnected ? 'Stripe Connected' : 'Payment Setup'}
                    </Typography>
                    <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                      {checkingStripeStatus
                        ? 'Loading status...'
                        : stripeConnected
                          ? 'Manage your payments'
                          : 'Connect with Stripe to accept payments'}
                    </Typography>
                  </Box>
                  {stripeConnected && (
                    <CheckCircleIcon sx={{ color: '#43a047', fontSize: 20 }} />
                  )}
                </Box>
              </ActionCard>
            </Grid>

            {stripeConnected && (
              <>
                <Grid item xs={6} sm={4}>
                  <ActionCard accentcolor="#2979ff" onClick={handleCreatePayment}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <StatIconWrap bgcolor="rgba(41,121,255,0.1)">
                        <PaymentIcon sx={{ fontSize: 22, color: '#2979ff' }} />
                      </StatIconWrap>
                      <Box>
                        <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: 'text.primary' }}>
                          Payment Link
                        </Typography>
                        <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                          Quick requests
                        </Typography>
                      </Box>
                    </Box>
                  </ActionCard>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <ActionCard
                    accentcolor="#7c4dff"
                    onClick={() => window.open('https://dashboard.stripe.com/', '_blank')}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <StatIconWrap bgcolor="rgba(124,77,255,0.1)">
                        <LaunchIcon sx={{ fontSize: 22, color: '#7c4dff' }} />
                      </StatIconWrap>
                      <Box>
                        <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: 'text.primary' }}>
                          Stripe Dashboard
                        </Typography>
                        <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                          Full management
                        </Typography>
                      </Box>
                    </Box>
                  </ActionCard>
                </Grid>
              </>
            )}
          </Grid>

          {/* ── Bottom Sections: Appointments + Low Stock ─────────────── */}
          <Grid container spacing={2}>
            {/* Upcoming Appointments */}
            {upcomingAppointments.length > 0 && (
              <Grid item xs={12} md={lowStockItems.length > 0 ? 7 : 12}>
                <SectionCard>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      p: 2.5,
                      pb: showAppointmentDetails ? 1.5 : 2.5,
                      cursor: 'pointer',
                    }}
                    onClick={() => setShowAppointmentDetails(!showAppointmentDetails)}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <StatIconWrap bgcolor="rgba(41,121,255,0.1)">
                        <CalendarTodayIcon sx={{ fontSize: 22, color: '#2979ff' }} />
                      </StatIconWrap>
                      <Box>
                        <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: 'text.primary' }}>
                          Upcoming Appointments
                        </Typography>
                        <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                          {upcomingAppointments.length} in the next 7 days
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        label={`${upcomingAppointments.length} upcoming`}
                        size="small"
                        sx={{
                          display: { xs: 'none', sm: 'flex' },
                          height: 24,
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          backgroundColor: 'rgba(41,121,255,0.1)',
                          color: '#2979ff',
                        }}
                      />
                      <IconButton size="small">
                        {showAppointmentDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </Box>
                  </Box>

                  <Collapse in={showAppointmentDetails}>
                    <Box sx={{ px: 2.5, pb: 2.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {upcomingAppointments.map((appointment, index) => (
                        <AppointmentListItem key={appointment.id || index}>
                          <Avatar
                            sx={{
                              width: 36,
                              height: 36,
                              fontSize: '0.85rem',
                              fontWeight: 700,
                              bgcolor: 'rgba(255,23,68,0.1)',
                              color: '#ff1744',
                            }}
                          >
                            {(appointment.client_name || 'U')[0].toUpperCase()}
                          </Avatar>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: 'text.primary' }}>
                                {appointment.client_name || 'Unknown Client'}
                              </Typography>
                              <Chip
                                label={formatAppointmentDate(appointment.date)}
                                size="small"
                                sx={{
                                  height: 20,
                                  fontSize: '0.65rem',
                                  fontWeight: 600,
                                  backgroundColor:
                                    formatAppointmentDate(appointment.date) === 'Today'
                                      ? 'rgba(255,23,68,0.1)'
                                      : formatAppointmentDate(appointment.date) === 'Tomorrow'
                                        ? 'rgba(255,109,0,0.1)'
                                        : 'rgba(0,0,0,0.06)',
                                  color:
                                    formatAppointmentDate(appointment.date) === 'Today'
                                      ? '#ff1744'
                                      : formatAppointmentDate(appointment.date) === 'Tomorrow'
                                        ? '#ff6d00'
                                        : 'text.secondary',
                                }}
                              />
                            </Box>
                            <Typography
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                fontSize: '0.8rem',
                                color: 'text.secondary',
                                mt: 0.25,
                              }}
                            >
                              <AccessTimeIcon sx={{ fontSize: 14 }} />
                              {appointment.time || formatAppointmentTime(appointment.date)}
                            </Typography>
                            {appointment.notes && (
                              <Typography
                                sx={{
                                  fontSize: '0.75rem',
                                  color: 'text.secondary',
                                  mt: 0.25,
                                  opacity: 0.8,
                                }}
                                noWrap
                              >
                                {appointment.notes.length > 60
                                  ? `${appointment.notes.substring(0, 60)}...`
                                  : appointment.notes}
                              </Typography>
                            )}
                          </Box>
                        </AppointmentListItem>
                      ))}

                      <Link to="/appointments" style={{ textDecoration: 'none' }}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 0.5,
                            pt: 1,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: '0.85rem',
                              fontWeight: 600,
                              color: '#ff1744',
                              '&:hover': { textDecoration: 'underline' },
                            }}
                          >
                            View All Appointments
                          </Typography>
                          <ArrowForwardIcon sx={{ fontSize: 16, color: '#ff1744' }} />
                        </Box>
                      </Link>
                    </Box>
                  </Collapse>
                </SectionCard>
              </Grid>
            )}

            {/* Low Stock Alert */}
            {lowStockItems.length > 0 && (
              <Grid item xs={12} md={upcomingAppointments.length > 0 ? 5 : 12}>
                <SectionCard>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      p: 2.5,
                      pb: showLowStockDetails ? 1.5 : 2.5,
                      cursor: 'pointer',
                    }}
                    onClick={() => setShowLowStockDetails(!showLowStockDetails)}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <StatIconWrap bgcolor="rgba(255,152,0,0.12)">
                        <WarningIcon sx={{ fontSize: 22, color: '#ff9800' }} />
                      </StatIconWrap>
                      <Box>
                        <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: 'text.primary' }}>
                          Low Stock Alert
                        </Typography>
                        <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                          {lowStockItems.length} item{lowStockItems.length !== 1 ? 's' : ''} running low
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        label={`${lowStockItems.length} item${lowStockItems.length !== 1 ? 's' : ''}`}
                        size="small"
                        sx={{
                          display: { xs: 'none', sm: 'flex' },
                          height: 24,
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          backgroundColor: 'rgba(255,152,0,0.12)',
                          color: '#ff9800',
                        }}
                      />
                      <IconButton size="small">
                        {showLowStockDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </Box>
                  </Box>

                  <Collapse in={showLowStockDetails}>
                    <Box sx={{ px: 2.5, pb: 2.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {lowStockItems.map((item) => {
                        const stockLevel = getStockLevel(item);
                        return (
                          <StockRow key={item.id}>
                            <InventoryIcon sx={{ fontSize: 20, color: '#ff9800' }} />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography
                                  sx={{ fontWeight: 600, fontSize: '0.9rem', color: 'text.primary' }}
                                  noWrap
                                >
                                  {item.name}
                                </Typography>
                                <Chip
                                  label={stockLevel.level}
                                  color={stockLevel.color}
                                  size="small"
                                  sx={{ height: 20, fontSize: '0.65rem' }}
                                />
                              </Box>
                              <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 0.25 }}>
                                {item.current_stock || 0} units remaining
                                {item.category_display ? ` · ${item.category_display}` : ''}
                              </Typography>
                            </Box>
                          </StockRow>
                        );
                      })}

                      <Link to="/inventory" style={{ textDecoration: 'none' }}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 0.5,
                            pt: 1,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: '0.85rem',
                              fontWeight: 600,
                              color: '#ff1744',
                              '&:hover': { textDecoration: 'underline' },
                            }}
                          >
                            Go to Inventory
                          </Typography>
                          <ArrowForwardIcon sx={{ fontSize: 16, color: '#ff1744' }} />
                        </Box>
                      </Link>
                    </Box>
                  </Collapse>
                </SectionCard>
              </Grid>
            )}
          </Grid>

          {/* ── Empty State: Nothing happening ───────────────────────── */}
          {upcomingAppointments.length === 0 && lowStockItems.length === 0 && (
            <SectionCard sx={{ mt: 0 }}>
              <Box sx={{ p: { xs: 3, md: 4 }, textAlign: 'center' }}>
                <Typography sx={{ fontSize: '2rem', mb: 1 }}>
                  &#128640;
                </Typography>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: '1.1rem',
                    color: 'text.primary',
                    mb: 0.5,
                  }}
                >
                  You're all set!
                </Typography>
                <Typography sx={{ fontSize: '0.9rem', color: 'text.secondary', mb: 2 }}>
                  No upcoming appointments or stock alerts. Use the quick links above to manage your studio.
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap' }}>
                  <Link to="/clients/add" style={{ textDecoration: 'none' }}>
                    <Button
                      variant="outlined"
                      size="small"
                      sx={{
                        borderRadius: 2,
                        borderColor: 'rgba(0,0,0,0.15)',
                        color: 'text.primary',
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '0.8rem',
                        '&:hover': { borderColor: '#ff1744', color: '#ff1744' },
                      }}
                    >
                      + Add Client
                    </Button>
                  </Link>
                  <Link to="/appointments" style={{ textDecoration: 'none' }}>
                    <Button
                      variant="outlined"
                      size="small"
                      sx={{
                        borderRadius: 2,
                        borderColor: 'rgba(0,0,0,0.15)',
                        color: 'text.primary',
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '0.8rem',
                        '&:hover': { borderColor: '#ff1744', color: '#ff1744' },
                      }}
                    >
                      + Schedule Appointment
                    </Button>
                  </Link>
                </Box>
              </Box>
            </SectionCard>
          )}
        </>
      )}
    </Box>
  );
};

export default Dashboard;